import { Check } from "lucide-react";
import { RESUME_TEMPLATES } from "@super-resume-new/resume-engine";
import type { ResumeTemplate } from "@super-resume-new/resume-engine";
import { cn } from "@super-resume-new/ui/lib/utils";
import { Badge } from "@super-resume-new/ui/components/badge";
import {
  PreviewCard,
  PreviewCardTrigger,
  PreviewCardPopup,
} from "@super-resume-new/ui/components/preview-card";

const TEMPLATE_LABELS: Record<ResumeTemplate, string> = {
  classic: "Classic",
  modern: "Modern",
  ats: "ATS",
  "soft-hard": "Soft Hard",
  acorn: "Acorn",
  finely: "Finely",
  imprecv: "Imprecv",
  simple: "Simple",
  ttq: "TTQ",
  timeline: "Timeline",
  executive: "Executive",
  lavandula: "Lavandula",
  nabcv: "NABCV",
};

const TEMPLATE_DESCRIPTIONS: Record<ResumeTemplate, string> = {
  classic: "Traditional single-column format with clean typography and standard layout.",
  modern: "Sleek contemporary layout with subtle accent styling and clear section hierarchy.",
  ats: "Optimized plain-text formatting for maximum ATS parser compatibility and scanning.",
  "soft-hard": "Structured design balancing soft skills and technical expertise seamlessly.",
  acorn: "Minimalist compact template tailored for tech professionals and developers.",
  finely: "Polished modern format featuring elegant section dividers and typography.",
  imprecv: "Bold high-impact layout suited for leadership, management, and executive roles.",
  simple: "Clean, distraction-free template suited for candidates across all industries.",
  ttq: "Technical-focused layout highlighting code achievements & measurable metrics.",
  timeline: "Chronological flow emphasizing career progression and project milestones.",
  executive: "Sophisticated design tailored for senior executives and domain leaders.",
  lavandula: "Soft-toned creative palette with distinct header focus and modern layout.",
  nabcv: "Balanced corporate format ideal for engineering, finance, and operations.",
};

type Props = {
  selected: ResumeTemplate;
  onChange: (template: ResumeTemplate) => void;
  disabled?: boolean;
  className?: string;
};

export function TemplatePicker({ selected, onChange, disabled, className }: Props) {
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Choose a template</h3>
        <span className="text-[11px] text-muted-foreground">Hover for preview</span>
      </div>

      {/* 2-Column Selection Grid with vertical scroll */}
      <div className="grid grid-cols-2 gap-2.5 max-h-[660px] overflow-y-auto pr-1">
        {RESUME_TEMPLATES.map((name) => {
          const isSelected = selected === name;
          return (
            <PreviewCard key={name}>
              <PreviewCardTrigger
                render={
                  <button
                    type="button"
                    id={`template-${name}`}
                    disabled={disabled}
                    onClick={() => onChange(name)}
                    className={cn(
                      "group flex w-full cursor-pointer flex-col items-center gap-1.5 rounded-xl border-2 border-border bg-card p-1.5 text-center transition disabled:cursor-not-allowed disabled:opacity-50",
                      isSelected
                        ? "border-brand bg-brand/10 shadow-xs ring-1 ring-brand/30"
                        : "hover:border-brand/40 hover:bg-accent/40",
                    )}
                  />
                }
              >
                <div
                  className="relative w-full overflow-hidden rounded-md bg-muted border border-border shadow-xs"
                  style={{ aspectRatio: "0.707" }}
                >
                  <img
                    src={`/templates/${name}.webp`}
                    alt={`${TEMPLATE_LABELS[name]} template`}
                    width={140}
                    height={198}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                  />
                  {isSelected && (
                    <div className="absolute inset-0 flex items-center justify-center bg-primary/20 backdrop-blur-[1px]">
                      <div className="rounded-full bg-primary p-1 text-white shadow-xs">
                        <Check size={14} className="stroke-[2.5]" aria-hidden="true" />
                      </div>
                    </div>
                  )}
                </div>
                <span className="text-[11px] font-semibold text-foreground truncate w-full px-0.5">
                  {TEMPLATE_LABELS[name]}
                </span>
              </PreviewCardTrigger>

              {/* Large PreviewCard Popup */}
              <PreviewCardPopup
                side="right"
                align="start"
                sideOffset={12}
                className="w-80 sm:w-96 flex flex-col gap-2.5 p-3.5 rounded-xl border border-border bg-popover/95 text-popover-foreground shadow-lg backdrop-blur-md z-50"
              >
                <div className="flex items-center justify-between border-b border-border pb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">
                      {TEMPLATE_LABELS[name]}
                    </span>
                    <Badge
                      variant="outline"
                      className="text-[10px] uppercase font-mono px-1.5 py-0"
                    >
                      Template
                    </Badge>
                  </div>
                  {isSelected && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] bg-brand/15 text-brand-foreground border-brand/30"
                    >
                      Selected
                    </Badge>
                  )}
                </div>

                <div
                  className="relative w-full overflow-hidden rounded-xl border border-border bg-muted shadow-xs"
                  style={{ aspectRatio: "0.707" }}
                >
                  <img
                    src={`/templates/${name}.webp`}
                    alt={`${TEMPLATE_LABELS[name]} enlarged preview`}
                    width={280}
                    height={396}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </div>

                <p className="text-xs text-muted-foreground leading-relaxed">
                  {TEMPLATE_DESCRIPTIONS[name]}
                </p>
              </PreviewCardPopup>
            </PreviewCard>
          );
        })}
      </div>
    </div>
  );
}
